import { Home } from "lucide-react";
import React, { useEffect, useState } from "react";

interface PricingLoadingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  minimumDisplayTime?: number; // milliseconds, default 4000
}

const loadingMessages = [
  "Analyzing your loan details...",
  "Calculating the best rates for you...",
];

const PricingLoadingModal: React.FC<PricingLoadingModalProps> = ({
  isOpen,
  onComplete,
  minimumDisplayTime = 4000,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isMinTimeElapsed, setIsMinTimeElapsed] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCurrentMessageIndex(0);
      setIsMinTimeElapsed(false);
      return;
    }

    // Rotate messages every 2 seconds
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000);

    // Set minimum display time
    const timer = setTimeout(() => {
      setIsMinTimeElapsed(true);
    }, minimumDisplayTime);

    return () => {
      clearInterval(messageInterval);
      clearTimeout(timer);
    };
  }, [isOpen, minimumDisplayTime]);

  useEffect(() => {
    if (isMinTimeElapsed && isOpen) {
      onComplete();
    }
  }, [isMinTimeElapsed, isOpen, onComplete]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-8 text-center">
        {/* Animated spinner */}
        <div className="flex justify-center mb-6">
          <div className="relative h-16 w-16">
            {/* Spinning ring */}
            <div className="absolute inset-0 animate-spin rounded-full border-4 border-primary border-t-transparent" />

            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Home className="h-8 w-8 text-primary/70" strokeWidth={2.25} />
            </div>
          </div>
        </div>

        {/* Loading message */}
        <h2 className="text-xl font-heading font-bold text-gray-900 mb-2">
          Finding Your Best Rates
        </h2>
        <p className="text-gray-600 min-h-[48px] flex items-center justify-center">
          {loadingMessages[currentMessageIndex]}
        </p>

        {/* Progress dots */}
        <div className="flex justify-center space-x-2 mt-6">
          {loadingMessages.map((_, index) => (
            <div
              key={index}
              className={`h-2 w-2 rounded-full transition-all duration-300 ${
                index === currentMessageIndex ? "bg-primary w-4" : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingLoadingModal;
